import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAcceptSignupInvite } from '../api/hooks.js';
import { setWorkspaceId } from '../workspace.js';
import TurnstileGate, { turnstileEnabled } from '../components/TurnstileGate.js';
import { ApiError } from '../api/client.js';

export default function AcceptSignupInvite() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [name, setName] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const accept = useAcceptSignupInvite();
  const navigate = useNavigate();

  const needsToken = turnstileEnabled();
  const blocked = needsToken && !turnstileToken;

  if (!token) {
    return (
      <div className="mx-auto mt-24 max-w-md text-center text-sm text-slate-600">
        This invite link is missing its token.
      </div>
    );
  }

  return (
    <div className="mx-auto mt-20 max-w-md px-6">
      <div className="card p-6">
        <h1 className="text-xl font-semibold text-slate-900">Welcome to adhc.to</h1>
        <p className="mt-1 text-sm text-slate-600">
          You've been invited to create an account. Pick your name and a workspace name to get started.
        </p>
        <form
          className="mt-5 space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            try {
              const res = await accept.mutateAsync({
                token,
                name,
                workspaceName,
                turnstileToken,
              });
              setWorkspaceId(res.workspace.id);
              navigate('/app/dashboard', { replace: true });
            } catch (err) {
              if (err instanceof ApiError) {
                const code = (err.payload as { error?: string })?.error ?? 'invalid_or_expired';
                setError(prettyError(code));
              } else {
                setError('Something went wrong.');
              }
            }
          }}
        >
          <div>
            <label className="label" htmlFor="name">Your name</label>
            <input
              id="name"
              type="text"
              required
              autoFocus
              maxLength={100}
              className="input mt-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="ws">Workspace name</label>
            <input
              id="ws"
              type="text"
              required
              maxLength={80}
              placeholder="e.g. Acme Marketing"
              className="input mt-1"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
            />
            <p className="mt-1 text-xs text-slate-500">
              You can rename it later — it's just where your short links live.
            </p>
          </div>
          <TurnstileGate onToken={setTurnstileToken} onError={setError} />
          {error && (
            <p className="rounded-md bg-red-50 p-2 text-sm text-red-700">{error}</p>
          )}
          <button
            type="submit"
            disabled={accept.isPending || blocked}
            className="btn-primary w-full"
            title={blocked ? 'Waiting for bot check…' : undefined}
          >
            {accept.isPending ? 'Creating account…' : 'Create account & workspace'}
          </button>
        </form>
      </div>
    </div>
  );
}

function prettyError(code: string): string {
  switch (code) {
    case 'invalid_or_expired':
      return 'This invite link is invalid or has expired. Ask the person who invited you for a fresh one.';
    case 'account_already_exists':
      return 'You already have an account. Go to /login instead.';
    case 'turnstile_failed':
      return 'Bot check failed. Refresh and try again.';
    default:
      return code.replace(/_/g, ' ');
  }
}
